import type { CriteriaChoiceUserType } from '~/model/CriteriaChoiceType';
import type { CriteriaType } from '~/model/CriteriaType';
import type { SportType } from '~/model/SportType';
import type { UserType } from '~/model/UserType';
import {
  CriteriaVisibilityStatus,
  RoleTypes,
} from '~/const/appConst';

export const buildSportCriteria = (
  criterias: CriteriaType[],
  sport: SportType,
  childId: number,
) => {
  return (
    criterias
    && criterias.length > 0
    && criterias.filter((c) => {
      return (
        c.sports
        && c.sports.length > 0
        && c.sports.includes(sport['@id'])
        && (childId
          ? c.visibility === CriteriaVisibilityStatus.VISIBLE
          : c.visibility !== CriteriaVisibilityStatus.HIDDEN)
      );
    })
  );
};

export const buildSportUserCriteria = (
  userCriterias: CriteriaChoiceUserType[],
  sportId: number,
  childId: number,
) => {
  return (
    userCriterias?.length > 0
    && userCriterias.filter((usc) => {
      const criteriaSportsIds
        = usc?.criterion.sports?.length > 0
          && (childId
            ? usc.criterion.visibility === CriteriaVisibilityStatus.VISIBLE
            : usc.criterion.visibility !== CriteriaVisibilityStatus.HIDDEN)
          && (usc.criterion.sports as SportType[]).map(s => s.id);
      return criteriaSportsIds && criteriaSportsIds.includes(sportId);
    })
  );
};

export const getMatchingCriteria = (
  user_criteria: CriteriaChoiceUserType,
  criterias: CriteriaType[],
) => {
  return (
    criterias
    && criterias.length > 0
    && criterias.find(c => c.id === user_criteria.criterion.id)
  );
};

export const getMatchingUserCriteria = (
  criteria: CriteriaType,
  userCriterias: CriteriaChoiceUserType[],
): CriteriaChoiceUserType =>
  userCriterias?.length > 0
    ? userCriterias.find(uc => uc.criterion?.id === criteria?.id)
    : null;

// If roles is an object instead of an array, convert it
const convertRolesObjectToArray = (
  roles: UserType['roles'],
): RoleTypes[] => {
  const isRoleObject
    = !Array.isArray(roles) && typeof roles === 'object' && roles !== null;
  return isRoleObject ? Object.values(roles) : roles || [];
};

const hasSuperAdminRole = (roles: RoleTypes[]): boolean =>
  convertRolesObjectToArray(roles)?.includes(RoleTypes.superAdmin);

const hasTrainerRole = (roles: RoleTypes[]): boolean =>
  convertRolesObjectToArray(roles)?.includes(RoleTypes.trainer);

const hasAdminRole = (roles: RoleTypes[]): boolean => {
  const userRoles = convertRolesObjectToArray(roles);
  return (
    userRoles?.includes(RoleTypes.adminCocrea)
    || userRoles?.includes(RoleTypes.adminTest)
  );
};

const getIsSuperAdmin = (user: Partial<UserType>): boolean => {
  return hasSuperAdminRole(user?.roles);
};

const getIsTrainer = (user: Partial<UserType>): boolean => {
  if (getIsSuperAdmin(user))
    return true;
  return hasTrainerRole(user?.roles);
};

export const isUserAdmin = (user: Partial<UserType>): boolean => {
  if (!user?.id)
    return false;
  if (getIsSuperAdmin(user))
    return true;
  if (getIsTrainer(user))
    return true;
  return hasAdminRole(user?.roles);
};

export const isSubmittable = (userUpdatingData, user) => {
  return (
    userUpdatingData
    && Boolean(userUpdatingData?.lastname)
    && Boolean(userUpdatingData?.firstname)
    && (user?.firstname !== userUpdatingData?.firstname
      || user?.lastname !== userUpdatingData?.lastname
      || user?.address !== userUpdatingData?.address
      || user?.city !== userUpdatingData?.city
      || user?.zipcode !== userUpdatingData?.zipcode
      || user?.phone !== userUpdatingData?.phone)
  );
};
